import { useEffect, useMemo, useState } from "react";
import axios, { CancelToken } from "axios";
import { useTranslation } from "react-i18next";

import { StatusCode } from "api/protocols";
import { GetProduct } from "api/rpc/2024-04/facilityAdmin/product/product";
import { IProduct, IVariant } from "redux/reducers/models/product";
import { LocaleCurrencyString } from "helpers/Locale";
import { useAppDispatch } from "hooks/redux";
import { showError } from "redux/actions/ui";

import { TSelectOption } from "components/select/SelectNew";

export type TBookingFeeProductSelection = {
  product: IProduct;
  variant: IVariant;
};

export function flattenBookingFeeProductOptions(
  products: IProduct[],
): TSelectOption<TBookingFeeProductSelection>[] {
  const options: TSelectOption<TBookingFeeProductSelection>[] = [];

  products?.forEach(product => {
    product?.variants?.forEach(variant => {
      const title = product?.preferred_title || product?.title || "";
      const label =
        variant?.title && variant.title !== product?.title ? `${title} - ${variant.title}` : title;

      options.push({
        id: variant.id,
        label,
        extraValue: { product, variant },
      });
    });
  });

  return options;
}

export function formatBookingFeeProductLabel(product: IProduct, variant: IVariant): string {
  const title = product?.preferred_title || product?.title;
  const name =
    variant?.title && variant.title !== product?.title ? `${title} - ${variant.title}` : title;

  return `${name} • ${LocaleCurrencyString({ amount: variant?.price })}`;
}

type UseBookingFeeProductsOptions = {
  enabled?: boolean;
};

export function useBookingFeeProducts(options: UseBookingFeeProductsOptions = {}) {
  const { enabled = true } = options;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [products, setProducts] = useState<IProduct[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const source = axios.CancelToken.source();

    async function loadProducts(token?: CancelToken) {
      setIsLoading(true);

      const res = await GetProduct({ type: "Booking Fee", extended_variants: true }, false, token);

      if (token?.reason) {
        return;
      }

      if (Number(res.status) !== Number(StatusCode.OK)) {
        dispatch(showError(t("Error getting booking fee products")));
        setProducts([]);
        setIsLoading(false);
        return;
      }

      setProducts(res.data ?? []);
      setIsLoading(false);
    }

    void loadProducts(source.token);

    return () => {
      source.cancel();
    };
  }, [dispatch, enabled, t]);

  const productOptions = useMemo(() => flattenBookingFeeProductOptions(products), [products]);

  return {
    products,
    productOptions,
    isLoading,
  };
}
